import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  BookmarkPlus, 
  Bookmark, 
  Plus, 
  Trash2, 
  Edit2, 
  Utensils, 
  Coffee, 
  Sun, 
  Moon, 
  Cookie,
  ChevronDown,
  ChevronUp,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const MealTemplates = ({ selectedDate, onMealLogged }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Create form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    meal_type: 'any',
    items: [{ food_name: '', calories: '', protein: '', carbs: '', fats: '', servings: 1 }]
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMealIcon = (type) => {
    switch (type) {
      case 'breakfast': return <Coffee className="w-4 h-4" />;
      case 'lunch': return <Sun className="w-4 h-4" />;
      case 'dinner': return <Moon className="w-4 h-4" />;
      case 'snack': return <Cookie className="w-4 h-4" />;
      default: return <Utensils className="w-4 h-4" />;
    }
  };

  const useTemplate = async (templateId, templateName) => {
    try {
      const response = await axios.post(`${API}/nutrition/templates/${templateId}/use`, null, {
        params: { date: selectedDate }
      });
      toast.success(`Added "${templateName}" to your log!`, {
        description: `${response.data.total_calories} cal, ${response.data.total_protein}g protein`
      });
      if (onMealLogged) onMealLogged();
    } catch (error) {
      toast.error('Failed to log meal template');
    }
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    
    try {
      await axios.delete(`${API}/nutrition/templates/${templateId}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const addItemToForm = () => {
    setNewTemplate({
      ...newTemplate,
      items: [...newTemplate.items, { food_name: '', calories: '', protein: '', carbs: '', fats: '', servings: 1 }]
    });
  };

  const removeItemFromForm = (index) => {
    if (newTemplate.items.length === 1) return;
    const newItems = newTemplate.items.filter((_, i) => i !== index);
    setNewTemplate({ ...newTemplate, items: newItems });
  };

  const updateFormItem = (index, field, value) => {
    const newItems = [...newTemplate.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setNewTemplate({ ...newTemplate, items: newItems });
  };

  const createTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    // Validate items
    const validItems = newTemplate.items.filter(item => 
      item.food_name.trim() && !isNaN(parseFloat(item.calories))
    );
    
    if (validItems.length === 0) {
      toast.error('Add at least one food item');
      return;
    }

    try {
      const payload = {
        name: newTemplate.name,
        meal_type: newTemplate.meal_type,
        items: validItems.map(item => ({
          food_name: item.food_name,
          calories: parseFloat(item.calories) || 0,
          protein: parseFloat(item.protein) || 0,
          carbs: parseFloat(item.carbs) || 0,
          fats: parseFloat(item.fats) || 0,
          servings: parseFloat(item.servings) || 1
        }))
      };

      await axios.post(`${API}/nutrition/templates`, payload);
      toast.success(`Template "${newTemplate.name}" created!`);
      
      // Reset form
      setNewTemplate({
        name: '',
        meal_type: 'any',
        items: [{ food_name: '', calories: '', protein: '', carbs: '', fats: '', servings: 1 }]
      });
      setShowCreateForm(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-500/30 p-4">
        <div className="animate-pulse">
          <div className="h-5 bg-amber-500/20 rounded w-1/3 mb-4"></div>
          <div className="space-y-2">
            <div className="h-12 bg-amber-500/20 rounded"></div>
            <div className="h-12 bg-amber-500/20 rounded"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-500/30 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bookmark className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-bold text-white">Meal Templates</h3>
          <span className="text-xs text-amber-400/70 bg-amber-500/20 px-2 py-0.5 rounded-full">
            {templates.length}
          </span>
        </div>
        <Button
          size="sm"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className={`${showCreateForm ? 'bg-stone-700' : 'bg-amber-600 hover:bg-amber-700'} text-white`}
        >
          {showCreateForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mb-4 p-4 bg-stone-900/50 rounded-lg border border-amber-500/30">
          <h4 className="text-sm font-semibold text-amber-300 mb-3">Create New Template</h4>
          
          {/* Template Name & Type */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Template name (e.g., My Breakfast)"
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-white text-sm col-span-2"
            />
            <select
              value={newTemplate.meal_type}
              onChange={(e) => setNewTemplate({ ...newTemplate, meal_type: e.target.value })}
              className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-white text-sm col-span-2"
            >
              <option value="any">Any Meal</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
            </select>
          </div>

          {/* Food Items */}
          <div className="space-y-2 mb-3">
            {newTemplate.items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  placeholder="Food name"
                  value={item.food_name}
                  onChange={(e) => updateFormItem(index, 'food_name', e.target.value)}
                  className="col-span-4 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <input
                  type="number"
                  placeholder="Cal"
                  value={item.calories}
                  onChange={(e) => updateFormItem(index, 'calories', e.target.value)}
                  className="col-span-2 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <input
                  type="number"
                  placeholder="P"
                  value={item.protein}
                  onChange={(e) => updateFormItem(index, 'protein', e.target.value)}
                  className="col-span-1 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <input
                  type="number"
                  placeholder="C"
                  value={item.carbs}
                  onChange={(e) => updateFormItem(index, 'carbs', e.target.value)}
                  className="col-span-1 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <input
                  type="number"
                  placeholder="F"
                  value={item.fats}
                  onChange={(e) => updateFormItem(index, 'fats', e.target.value)}
                  className="col-span-1 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <input
                  type="number"
                  placeholder="Srv"
                  value={item.servings}
                  onChange={(e) => updateFormItem(index, 'servings', e.target.value)}
                  className="col-span-2 bg-stone-800 border border-stone-700 rounded px-2 py-1.5 text-white text-xs"
                />
                <button
                  onClick={() => removeItemFromForm(index)}
                  className="col-span-1 text-red-400 hover:text-red-300 p-1"
                  disabled={newTemplate.items.length === 1}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-between">
            <Button
              size="sm"
              variant="outline"
              onClick={addItemToForm}
              className="text-amber-400 border-amber-500/50"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
            <Button
              size="sm"
              onClick={createTemplate}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Check className="w-4 h-4 mr-1" /> Save Template
            </Button>
          </div>
        </div>
      )}

      {/* Templates List */}
      {templates.length === 0 ? (
        <div className="text-center py-6 text-stone-400">
          <BookmarkPlus className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No meal templates yet</p>
          <p className="text-xs mt-1">Create one to quickly log frequent meals!</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-stone-900/50 rounded-lg border border-stone-700 hover:border-amber-500/50 transition-colors"
            >
              {/* Template Header */}
              <div className="flex items-center justify-between p-3">
                <div 
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                >
                  <div className="text-amber-400">
                    {getMealIcon(template.meal_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{template.name}</p>
                    <p className="text-xs text-stone-400">
                      {template.total_calories} cal • {template.total_protein}g protein • {template.items?.length || 0} items
                    </p>
                  </div>
                  {expandedTemplate === template.id ? (
                    <ChevronUp className="w-4 h-4 text-stone-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-stone-400" />
                  )}
                </div>
                
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    size="sm"
                    onClick={() => useTemplate(template.id, template.name)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Log
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteTemplate(template.id, template.name)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedTemplate === template.id && (
                <div className="px-3 pb-3 border-t border-stone-700 pt-2">
                  <p className="text-xs text-stone-500 mb-2 uppercase tracking-wider">Items in this template:</p>
                  <div className="space-y-1">
                    {template.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-stone-300">{item.food_name}</span>
                        <span className="text-stone-500">
                          {item.calories} cal • {item.protein}g P
                        </span>
                      </div>
                    ))}
                  </div>
                  {template.use_count > 0 && (
                    <p className="text-xs text-amber-400/60 mt-2">
                      Used {template.use_count} time{template.use_count > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default MealTemplates;
